import React, { useState, useEffect, useRef } from 'react';
import { ThemeConfig } from '../types';

interface QuestionProps {
  theme: ThemeConfig;
}

interface Confetti {
  x: number;
  y: number;
  r: number;
  color: string;
}

const Question: React.FC<QuestionProps> = ({ theme }) => {
  const [answered, setAnswered] = useState(false);
  const [noCount, setNoCount] = useState(0);
  const [noPosition, setNoPosition] = useState({ x: 0, y: 0 });
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const noBtnRef = useRef<HTMLButtonElement>(null);
  const yesBtnRef = useRef<HTMLButtonElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const confettiRef = useRef<Confetti[]>([]);
  const animationRef = useRef<number | undefined>(undefined);

  const GAP = 24;

  const directions = [
    { x: -120, y: 0 },
    { x: 120, y: 0 },
    { x: 0, y: -80 },
    { x: 0, y: 80 },
    { x: -120, y: -80 },
    { x: 120, y: -80 },
    { x: -120, y: 80 },
    { x: 120, y: 80 }
  ];

  useEffect(() => {
    // Center buttons initially
    const centerButtons = () => {
      if (!buttonsRef.current || !yesBtnRef.current || !noBtnRef.current) return;
      
      const containerWidth = buttonsRef.current.clientWidth;
      const centerX = containerWidth / 2;

      setNoPosition({
        x: centerX + GAP / 2,
        y: 10
      });
    };

    setTimeout(centerButtons, 50);
    window.addEventListener('resize', centerButtons);
    
    return () => {
      window.removeEventListener('resize', centerButtons);
    };
  }, []);

  const overlapsYes = (x: number, y: number): boolean => {
    if (!yesBtnRef.current || !noBtnRef.current || !buttonsRef.current) return false;

    const yesRect = yesBtnRef.current.getBoundingClientRect();
    const noRect = noBtnRef.current.getBoundingClientRect();
    const containerRect = buttonsRef.current.getBoundingClientRect();

    const noLeft = containerRect.left + x;
    const noTop = containerRect.top + y;
    const noRight = noLeft + noRect.width;
    const noBottom = noTop + noRect.height;

    return !(
      noRight < yesRect.left - GAP ||
      noLeft > yesRect.right + GAP ||
      noBottom < yesRect.top - GAP ||
      noTop > yesRect.bottom + GAP
    );
  };

  const handleNoHover = () => {
    if (!buttonsRef.current || !noBtnRef.current) return;

    setNoCount(prev => prev + 1);

    const containerWidth = buttonsRef.current.clientWidth;
    const containerHeight = buttonsRef.current.clientHeight;

    for (let i = 0; i < 10; i++) {
      const dir = directions[Math.floor(Math.random() * directions.length)];

      let newX = noPosition.x + dir.x;
      let newY = noPosition.y + dir.y;

      newX = Math.max(0, Math.min(newX, containerWidth - noBtnRef.current.offsetWidth));
      newY = Math.max(0, Math.min(newY, containerHeight - noBtnRef.current.offsetHeight));

      if (!overlapsYes(newX, newY)) {
        setNoPosition({ x: newX, y: newY });
        break;
      }
    }
  };

  const createConfetti = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    confettiRef.current = [];
    
    for (let i = 0; i < 150; i++) {
      confettiRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        r: Math.random() * 6 + 4,
        color: `hsl(${Math.random() * 360},100%,60%)`
      });
    }
  };

  const drawConfetti = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    confettiRef.current.forEach(p => {
      ctx.beginPath();
      ctx.fillStyle = p.color;
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
      p.y += 3;
      if (p.y > canvas.height) p.y = -10;
    });

    animationRef.current = requestAnimationFrame(drawConfetti);
  };

  const handleYesClick = () => {
    setAnswered(true);
    createConfetti();
    drawConfetti();
  };

  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const tooltipText = noCount > 0
    ? `You tried clicking on NO ${noCount} times -_-`
    : "You never clicked on NO. Good :)";

  return (
    <div className="relative w-full min-h-[calc(100vh-200px)] flex items-center justify-center overflow-hidden">
      {/* Mobile Warning */}
      <div className="md:hidden text-center px-5 text-xl text-gray-700">
        💻 This experience is best enjoyed on a desktop.<br /><br />
        Please open this on a PC 😉
      </div>

      {/* Canvas for confetti */}
      <canvas 
        ref={canvasRef}
        className="fixed top-0 left-0 pointer-events-none z-10"
      />

      {/* Card */}
      <div className="hidden md:block bg-white rounded-2xl shadow-2xl p-8 w-[480px] h-[330px] text-center relative z-20">
        {!answered ? (
          <>
            <h1 className="text-3xl font-semibold text-[#ff4f7b] mb-6">
              Will you be my Valentine?
            </h1>

            <div ref={buttonsRef} className="relative h-[150px] w-full mt-10">
              {/* Yes Button */}
              <button
                ref={yesBtnRef}
                onClick={handleYesClick}
                className="absolute w-[100px] h-11 text-base rounded-full border-none cursor-pointer bg-[#ff4f7b] text-white hover:bg-[#ff2f64] hover:scale-105 transition-all duration-100"
                style={{
                  left: `calc(50% - ${100 + GAP / 2}px)`,
                  bottom: '10px'
                }}
              >
                YES
              </button>

              {/* No Button */}
              <button
                ref={noBtnRef}
                onMouseEnter={handleNoHover}
                className="absolute w-[100px] h-11 text-base rounded-full border-none cursor-pointer bg-gray-300 text-gray-600 transition-all duration-100"
                style={{
                  left: `${noPosition.x}px`,
                  top: `${noPosition.y}px`
                }}
              >
                NO
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-semibold text-[#ff4f7b] mb-6">
              Excellent Choice!
            </h1>
            <div className="text-lg text-gray-700 mt-3 leading-relaxed">
              Welcome to being my Valentine.
            </div>
            <div className="text-4xl mt-3">
              🩷 🩷 🩷 🩷
            </div>

            {/* Info Icon with Tooltip */}
            <div className="absolute bottom-3 right-3 w-6 h-6 rounded-full bg-[#ff4f7b] text-white text-sm font-bold flex items-center justify-center cursor-default group">
              i
              <div className="absolute bottom-8 right-0 bg-black/85 text-white px-3 py-2 rounded-lg text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                {tooltipText}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Question;
